import * as React from "react"
import { type WorkItem, type WorkItemTransaction } from "@digits-graphql/frontend/graphql-bearer"
import { IconSize } from "@digits-shared/components/UI/Icons/Icon"
import { squareIconStyles } from "@digits-shared/components/UI/Icons/squareIcons"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled, { css, keyframes } from "styled-components"
import { useUpdateParty } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/Actions/Shared"
import { FIELD_PUSHED } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/Shared/TransactionReviewWorkItemRowSharedElements"
import {
  UNKNOWN_CATEGORY,
  UNKNOWN_PARTY,
} from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/sharedRowStyles"
import {
  usePartyState,
  VendorDetail,
} from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/WorkItemDetails/VendorDetail"
import { useWorkItemsContext } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemsContext"
import { useTextIsOverflowing } from "src/frontend/hooks/useTextIsOverflowing"
import { FrontendPartyRole } from "src/frontend/types/FrontendPartyRole"
import { PartyIcon } from "src/shared/components/PartyHover/PartyIcon"

/*
 * STYLES
 */

const PUSH_FIELD_KEYFRAMES = keyframes`
  10%, 90% {
    outline: 1.5px solid ${colors.translucentPrimary70};
    border-color: ${colors.primary};
    box-shadow: 0 0 20px ${colors.translucentPrimary30};
  }
  0%, 100% {
    outline: 1.5px solid ${colors.translucentPrimary10};
    border-color: inherit;
    box-shadow: none;
  }
`

export const Party = styled.div`
  position: relative;
  flex: 1;
  gap: 8px;
  padding-left: 34px;
  margin-right: 10px;
`

export const Icon = styled.div<{ isClosed?: boolean }>`
  ${squareIconStyles(10)}
  position: absolute;
  left: -28px;
  top: -6px;
  filter: drop-shadow(0px 1px 14px rgba(0, 0, 0, 0.25));

  ${({ isClosed }) =>
    isClosed &&
    css`
      filter: grayscale(100%);
    `};

  &.${FIELD_PUSHED} {
    border-radius: 10px;
    transition:
      border-color 100ms,
      box-shadow 200ms,
      outline-color 200ms;
    animation: ${PUSH_FIELD_KEYFRAMES} 2s ease-in-out forwards;
  }
`

export const PartyInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
`

export const PartyTitle = styled.div<{ unassigned: boolean }>`
  font-size: 15px;
  color: ${colors.secondary};

  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: 180px;
  font-weight: ${({ unassigned }) => (unassigned ? fonts.weight.book : fonts.weight.medium)};
`

export const PartySubtitle = styled.div<{ unassigned: boolean }>`
  font-size: 11px;
  color: ${({ unassigned }) => (unassigned ? colors.secondary : colors.translucentSecondary60)};

  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font-weight: ${({ unassigned }) => (unassigned ? fonts.weight.heavy : fonts.weight.book)};
`

/*
 * INTERFACES
 */

interface WorkItemTransactionPartyDetailsProps {
  transaction?: WorkItemTransaction
  iconSize?: IconSize
}

/*
 * COMPONENTS
 */

export const WorkItemPartyDetails: React.FC<WorkItemTransactionPartyDetailsProps> = ({
  transaction,
  iconSize = IconSize.MediumLarger,
}) => {
  const party = transaction?.party
  const category = transaction?.displayCategory

  const partyRole = usePartyRole(transaction)
  const { isTextOverflowing, textElementRef } = useTextIsOverflowing<HTMLDivElement>()

  return (
    <Party>
      <Icon>
        <PartyIcon party={party || { name: UNKNOWN_PARTY }} size={iconSize} />
      </Icon>
      <PartyInfo>
        <DigitsTooltip
          content={party?.name || partyRole}
          disabled={!isTextOverflowing}
          side="top"
          align="start"
        >
          <PartyTitle ref={textElementRef} unassigned={!party?.name}>
            {party?.name || partyRole}
          </PartyTitle>
        </DigitsTooltip>
        <PartySubtitle unassigned={!category?.name}>
          {category?.name || UNKNOWN_CATEGORY}
        </PartySubtitle>
      </PartyInfo>
    </Party>
  )
}

export const EditableWorkItemPartyDetails: React.FC<
  WorkItemTransactionPartyDetailsProps & { workItem: WorkItem }
> = ({ transaction, workItem, iconSize = IconSize.MediumLarger }) => {
  const { isClosed } = useWorkItemsContext()
  const party = transaction?.party
  const partyRole = usePartyRole(transaction)
  const updateParty = useUpdateParty(workItem)
  const { classes } = usePartyState(workItem)

  if (!transaction) {
    return (
      <Party>
        <Icon>
          <PartyIcon party={party || { name: UNKNOWN_PARTY }} size={iconSize} />
        </Icon>
        <PartyInfo>
          <PartyTitle unassigned={!party?.name}>{party?.name || partyRole}</PartyTitle>
        </PartyInfo>
      </Party>
    )
  }

  return (
    <Party>
      <Icon isClosed={isClosed} className={classes}>
        <PartyIcon party={updateParty || party || { name: UNKNOWN_PARTY }} size={iconSize} />
      </Icon>
      <VendorDetail workItem={workItem} />
    </Party>
  )
}

function usePartyRole(transaction?: WorkItemTransaction) {
  return React.useMemo(() => {
    if (transaction?.displayPartyRole) {
      const frontendRole = FrontendPartyRole.findByRole(transaction.displayPartyRole)
      if (frontendRole !== FrontendPartyRole.Unknown) {
        return `Missing ${frontendRole.displayName}`
      }
    }

    return UNKNOWN_PARTY
  }, [transaction?.displayPartyRole])
}
